/**
 * "Embedded" is a property of the URL, not of the session.
 *
 * A session flag cannot do this job: one browser has one cookie jar, so a normal
 * login in another tab would clobber the flag for a live embed. The SSO exchange
 * lands the customer on {@link EMBED_PORTAL_PREFIX} instead, and middleware
 * rewrites that onto the real portal route while setting
 * {@link EMBED_REQUEST_HEADER}.
 */

/** Route group that is framable by a provider. */
export const EMBED_PREFIX = '/embed'

export const EMBED_PORTAL_PREFIX = `${EMBED_PREFIX}/portal`

/**
 * Set by middleware on the rewritten request, read by server components. Never
 * trust an inbound value: middleware runs on every matched path and always
 * overwrites what the client sent.
 */
export const EMBED_REQUEST_HEADER = 'x-gcx-embed'

/** Self-fetch that refreshes an ordinary session. */
export const SESSION_ROUTE = '/auth/session'

/**
 * Self-fetch that refreshes an embedded portal session.
 *
 * A separate route rather than a header on {@link SESSION_ROUTE}: the refresh
 * has to know which cookie it is rewriting, and a route that trusted an inbound
 * {@link EMBED_REQUEST_HEADER} would break that header's invariant.
 */
export const EMBED_SESSION_ROUTE = `${EMBED_PREFIX}/session`

export const sessionRouteFor = (isEmbed: boolean): string =>
  isEmbed ? EMBED_SESSION_ROUTE : SESSION_ROUTE

/**
 * Posted to the parent frame by `/embed/session-expired` so a provider can
 * re-mint a token and reload the iframe. Only the provider can recover.
 *
 * Cross-repo contract: the listener hardcodes this string in the provider's own
 * codebase, so changing the value is a breaking change.
 */
export const EMBED_SESSION_EXPIRED_MESSAGE = 'guidecx:embed-session-expired'

/**
 * Framable landing spot for an SSO exchange that could not be completed. The
 * exchange is framed directly by the provider, so returning a JSON body there
 * would show the customer a raw error object inside someone else's product.
 */
export const EMBED_LOGIN_FAILED_ROUTE = `${EMBED_PREFIX}/login-failed`

/**
 * Coarser than the status code on purpose: the page only has to answer "can I do
 * anything about this?" - a link that will never work, an access decision only
 * the provider can change, or a failure worth retrying.
 */
export const EMBED_LOGIN_FAILURE_REASONS = [
  'invalid-link',
  'not-authorized',
  'unexpected',
] as const

export type EmbedLoginFailureReason =
  (typeof EMBED_LOGIN_FAILURE_REASONS)[number]

export const EMBED_LOGIN_FAILURE_PARAM = 'reason'

/**
 * Unrecognised codes become `unexpected` rather than throwing: this only runs
 * once the exchange has already failed.
 */
export const embedLoginFailureReason = (
  statusCode?: number,
): EmbedLoginFailureReason => {
  if (statusCode === 400) return 'invalid-link'
  if (statusCode === 403) return 'not-authorized'
  return 'unexpected'
}

/**
 * Relative on purpose. The success redirect is absolute so the session cookie
 * reaches the workspace host, but a failure has no session and no workspace.
 *
 * The reason is caller-controlled: read it back with
 * {@link toEmbedLoginFailureReason}, never as a raw string.
 */
export const embedLoginFailedTarget = (
  reason: EmbedLoginFailureReason,
): string =>
  `${EMBED_LOGIN_FAILED_ROUTE}?${EMBED_LOGIN_FAILURE_PARAM}=${reason}`

/** Narrows an inbound `reason` param, which anyone can craft, to known copy. */
export const toEmbedLoginFailureReason = (
  value?: string,
): EmbedLoginFailureReason =>
  EMBED_LOGIN_FAILURE_REASONS.includes(value as EmbedLoginFailureReason)
    ? (value as EmbedLoginFailureReason)
    : 'unexpected'

export const isEmbedPortalPath = (pathname?: string | null): boolean =>
  !!pathname && pathname.startsWith(EMBED_PORTAL_PREFIX)

/**
 * Every link inside the portal has to go through this. One that drops the prefix
 * takes the customer out of the embed mid-session and the app navigation
 * reappears inside the provider's page.
 */
export const toEmbedPortalPath = (portalPath: string): string =>
  portalPath.startsWith('/portal') ? `${EMBED_PREFIX}${portalPath}` : portalPath

/** Strips the prefix back off, for the middleware rewrite onto the real route. */
export const fromEmbedPortalPath = (embedPath: string): string =>
  isEmbedPortalPath(embedPath)
    ? embedPath.slice(EMBED_PREFIX.length)
    : embedPath

/** Framable dead end for an embedded navigation with no portal equivalent. */
export const EMBED_UNAVAILABLE_ROUTE = `${EMBED_PREFIX}/unavailable`

const APP_PROJECT_PATH = /^\/project\/([0-9a-f-]{36})(?:\/|$)/i

/**
 * Where to send an embedded customer who has navigated off `/embed/portal/*`.
 *
 * Answers only *where* an escape goes, never *whether* a request is one - the
 * caller owns that. Total on purpose: a prefix allowlist here would be a second
 * copy of the middleware's `protectedPaths` and would drift from it, and every
 * path it failed to cover would fall through to `/auth/logout`, destroying a
 * live embed session over a bad href.
 *
 * A safety net, not a second router. A link that needs a specific destination
 * has to be fixed at the link.
 */
export const embedEscapeTarget = (pathname: string, search = ''): string => {
  // Same route with the prefix restored, so the parameters still describe the
  // page the customer was on.
  if (pathname.startsWith('/portal'))
    return `${toEmbedPortalPath(pathname)}${search}`

  const projectId = pathname.match(APP_PROJECT_PATH)?.[1]

  // Mappings onto a different route drop `search`: parameters written for
  // /project/:id mean nothing on the portal's today page or on the dead end.
  if (projectId) return `${EMBED_PORTAL_PREFIX}/${projectId}/today`

  return EMBED_UNAVAILABLE_ROUTE
}
